import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ExpenseCategoryDto } from '../../services/generalExpensesService';

interface CategoryAutocompleteProps {
  categories: ExpenseCategoryDto[];
  selectedId: number | null;
  selectedName: string;
  onChange: (id: number | null, name: string) => void;
  // יוצר קטגוריה חדשה בשרת ומחזיר אותה (כולל המקרה שכבר קיימת - מוחזרת הקיימת)
  onCreateCategory: (name: string) => Promise<ExpenseCategoryDto>;
  onBlur?: () => void;
  hasError?: boolean;
  disabled?: boolean;
}

const Wrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => (props.$hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  box-shadow: var(--shadow-medium);
  max-height: 220px;
  overflow-y: auto;
  z-index: 20;
`;

const Option = styled.li<{ $active?: boolean }>`
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  background: ${props => (props.$active ? 'var(--hover-background)' : 'transparent')};
  text-align: right;

  &:hover {
    background: var(--hover-background);
  }
`;

const CreateHint = styled.li`
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--primary-color);
  font-weight: 500;
  border-top: 1px solid var(--border-color-light);
  text-align: right;

  &:hover {
    background: var(--hover-background);
  }
`;

const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  categories,
  selectedName,
  onChange,
  onCreateCategory,
  onBlur,
  hasError,
  disabled,
}) => {
  const [text, setText] = useState(selectedName);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(selectedName);
  }, [selectedName]);

  // סגירת ה-dropdown בלחיצה מחוץ לרכיב
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const trimmed = text.trim();
  const filtered = trimmed
    ? categories.filter(c => c.name.toLowerCase().includes(trimmed.toLowerCase()))
    : categories;
  const exactMatch = categories.find(c => c.name.toLowerCase() === trimmed.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setText(value);
    setOpen(true);
    // בכל שינוי - מאפסים את הבחירה עד שתיבחר/תיווצר קטגוריה תקפה
    onChange(null, value);
  };

  const selectCategory = (cat: ExpenseCategoryDto) => {
    setText(cat.name);
    onChange(cat.id, cat.name);
    setOpen(false);
  };

  const createAndSelect = async () => {
    if (!trimmed || creating) return;
    if (exactMatch) {
      selectCategory(exactMatch);
      return;
    }
    try {
      setCreating(true);
      const created = await onCreateCategory(trimmed);
      selectCategory(created);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void createAndSelect();
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Input
        type="text"
        value={text}
        placeholder="הקלד/י לחיפוש או הוספת קטגוריה חדשה"
        $hasError={hasError}
        disabled={disabled}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        autoComplete="off"
      />
      {open && (filtered.length > 0 || (trimmed && !exactMatch)) && (
        <Dropdown>
          {filtered.map(cat => (
            <Option
              key={cat.id}
              // מונע איבוד פוקוס לפני בחירה
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectCategory(cat)}
            >
              {cat.name}
            </Option>
          ))}
          {trimmed && !exactMatch && (
            <CreateHint
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void createAndSelect()}
            >
              {creating ? 'יוצר קטגוריה...' : `+ הוסף קטגוריה חדשה: "${trimmed}"`}
            </CreateHint>
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default CategoryAutocomplete;
